import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import './Checkout.css';
import { updateCartCustomerInfo, saveCartToBackend } from '../utils/cartTracker';
import { getParsedUser } from '../utils/authStorage';
import {
  generateOrderId,
  getEstimatedDeliveryDate,
  persistLastOrder,
} from '../utils/orderCompletion';

const Checkout = ({ navigate: navigateProp }) => {
  const location = useLocation();
  const navigate = navigateProp || ((path) => { window.location.href = path; });
  const [cartItems, setCartItems] = useState([]);
  
  // Get cart from localStorage if URL params are empty
  const getCartFromStorage = () => {
    const savedCart = localStorage.getItem('raheasy_cart');
    return savedCart ? JSON.parse(savedCart) : [];
  };

  // Initialize cart items - always read from localStorage to avoid URI_TOO_LONG errors
  useEffect(() => {
    // Always read from localStorage (cart is already saved there)
    const items = location.state?.cartItems || getCartFromStorage();
    setCartItems(items);
    
    // Clean up any items parameter from URL if present (to avoid URI_TOO_LONG)
    const urlParams = new URLSearchParams(location.search);
    if (urlParams.has('items')) {
      window.history.replaceState({}, '', '/checkout');
    }
  }, [location.search, location.state]);
  
  const deliveryOptions = [
    {
      id: 'same-day',
      label: 'Same-day Delivery',
      price: 500,
      description: 'Orders placed before 12:00 PM delivered today within Nairobi.'
    },
    {
      id: 'next-day',
      label: 'Next-day / Scheduled Delivery',
      price: 500,
      description: 'Orders placed after 12:00 PM or scheduled deliveries at your preferred time.'
    },
    {
      id: 'free',
      label: 'Free Delivery (Subject to confirmation)',
      price: 0,
      description: 'Available for qualifying orders or promotions. Confirmed after communication with our team.'
    }
  ];
  const [paymentMethod, setPaymentMethod] = useState('mpesa');
  const [mpesaNumber, setMpesaNumber] = useState('');
  const [cardDetails, setCardDetails] = useState({
    cardNumber: '',
    expiryDate: '',
    cvv: '',
    cardName: ''
  });
  const getUserInfo = () => {
    const u = getParsedUser();
    if (!u) return null;
    const { token: _t, ...rest } = u;
    return rest;
  };

  const userInfo = getUserInfo();
  
  const [deliveryInfo, setDeliveryInfo] = useState(() => {
    // Populate from user info if available
    const user = getUserInfo();
    return {
      name: user?.name || '',
      email: user?.email || '',
      phone: user?.phone || '',
      address: '',
      city: 'Nairobi'
    };
  });
  const [selectedDeliveryOption, setSelectedDeliveryOption] = useState(deliveryOptions[0]);

  // Save customer info to cart when delivery info changes
  useEffect(() => {
    if (deliveryInfo.name || deliveryInfo.email || deliveryInfo.phone || deliveryInfo.address) {
      updateCartCustomerInfo(deliveryInfo);
    }
  }, [deliveryInfo]);

  // Filter available items and calculate totals (defined here for use in functions below)
  const isCartItemPurchasable = (item) =>
    item.inStock !== false && item.isAvailable !== false;
  const availableItems = cartItems.filter(isCartItemPurchasable);
  const unavailableItems = cartItems.filter((item) => !isCartItemPurchasable(item));

  // Get product image with priority: thumbnail > primary image > first image > fallback
  const getProductImage = (item) => {
    if (!item) return '/images/default-product.png';
    
    // Priority 1: Use thumbnail if available
    if (item.thumbnail) {
      return item.thumbnail;
    }
    
    // Priority 2: Use primary image from images array
    if (item.images && Array.isArray(item.images) && item.images.length > 0) {
      const primaryIndex = item.primaryImageIndex !== undefined && item.primaryImageIndex < item.images.length
        ? item.primaryImageIndex
        : 0;
      return item.images[primaryIndex];
    }
    
    // Priority 3: Fallback to single image field
    if (item.image) {
      return item.image;
    }
    
    // Priority 4: Default placeholder
    return '/images/default-product.png';
  };

  const [imageErrors, setImageErrors] = useState({});

  const handleImageError = (itemId) => {
    setImageErrors(prev => ({ ...prev, [itemId]: true }));
  };

  const calculateTotal = () => {
    // Only calculate total for available items
    return availableItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };
  const deliveryCost = selectedDeliveryOption.price;

  const validateDeliveryInfo = () => {
    if (!deliveryInfo.name?.trim() || !deliveryInfo.phone?.trim() || !deliveryInfo.address?.trim()) {
      alert('Please complete all required delivery fields before placing your order.');
      return false;
    }
    return true;
  };

  const finishOrder = (paymentMethod) => {
    if (!validateDeliveryInfo()) return;
    if (availableItems.length === 0) {
      alert('No available items in your cart to order.');
      return;
    }

    const subtotal = calculateTotal();
    const order = {
      orderId: generateOrderId(),
      email: deliveryInfo.email?.trim() || '',
      estimatedDeliveryDate: getEstimatedDeliveryDate(selectedDeliveryOption.id),
      deliveryCountry: 'KE',
      deliveryOption: selectedDeliveryOption.label,
      paymentMethod,
      items: availableItems,
      subtotal,
      deliveryCost,
      total: subtotal + deliveryCost,
      deliveryInfo: { ...deliveryInfo },
      createdAt: new Date().toISOString(),
    };

    persistLastOrder(order);
    localStorage.removeItem('raheasy_cart');
    saveCartToBackend([]);
    setCartItems([]);
    navigate('/order-confirmation', { state: { order } });
  };

  const handleMpesaPayment = (e) => {
    e.preventDefault();
    if (!mpesaNumber || !mpesaNumber.startsWith('0') || mpesaNumber.length !== 10) {
      alert('Please enter a valid M-Pesa number starting with 0 (10 digits)');
      return;
    }
    finishOrder('mpesa');
  };

  const handleCardPayment = (e) => {
    e.preventDefault();
    finishOrder('card');
  };

  const handleWhatsAppOrder = () => {
    finishOrder('whatsapp');
  };

  // Initialize cart items
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const itemsParam = urlParams.get('items');
    let items = [];
    
    if (itemsParam) {
      try {
        items = JSON.parse(decodeURIComponent(itemsParam));
      } catch (e) {
        items = getCartFromStorage();
      }
    } else {
      items = location.state?.cartItems || getCartFromStorage();
    }
    
    setCartItems(items);
  }, [location.search, location.state]);

  // Remove item from cart
  const handleRemoveItem = (itemId) => {
    const updatedCart = cartItems.filter(item => item.id !== itemId);
    setCartItems(updatedCart);
    localStorage.setItem('raheasy_cart', JSON.stringify(updatedCart));
    saveCartToBackend(updatedCart);
    
    // Navigate away if cart is empty, otherwise stay on checkout
    // Don't update URL with items to avoid URI_TOO_LONG errors
    if (updatedCart.length === 0) {
      navigate('/');
    }
  };

  if (cartItems.length === 0) {
    return (
      <div className="checkout-empty">
        <h2>Your cart is empty</h2>
        <button onClick={() => navigate('/')} className="continue-shopping-btn">
          Continue Shopping
        </button>
      </div>
    );
  }

  return (
    <div className="checkout-page">
      <div className="checkout-container">
        <h1 className="checkout-title">Checkout</h1>
        
        <div className="checkout-content">
          <div className="checkout-left">
            <div className="checkout-section">
              <h2>Delivery Information</h2>
              <form className="delivery-form">
                <div className="form-row">
                  <div className="form-group">
                    <label>Full Name *</label>
                    <input
                      type="text"
                      value={deliveryInfo.name}
                      onChange={(e) => setDeliveryInfo({...deliveryInfo, name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Email (Optional)</label>
                    <input
                      type="email"
                      value={deliveryInfo.email}
                      onChange={(e) => setDeliveryInfo({...deliveryInfo, email: e.target.value})}
                      placeholder="Enter email for order updates (optional)"
                    />
                    <small className="form-hint">
                      Optional — order updates and Google customer feedback surveys
                    </small>
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Phone Number *</label>
                    <input
                      type="tel"
                      value={deliveryInfo.phone}
                      onChange={(e) => setDeliveryInfo({...deliveryInfo, phone: e.target.value})}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>City *</label>
                    <input
                      type="text"
                      value={deliveryInfo.city}
                      onChange={(e) => setDeliveryInfo({...deliveryInfo, city: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="form-group">
                  <label>Delivery Address *</label>
                  <textarea
                    value={deliveryInfo.address}
                    onChange={(e) => setDeliveryInfo({...deliveryInfo, address: e.target.value})}
                    rows="3"
                    required
                  />
                </div>
              </form>
            </div>

            <div className="checkout-section">
              <h2>Delivery Options</h2>
              <div className="delivery-options-list">
                {deliveryOptions.map(option => (
                  <label
                    key={option.id}
                    className={`delivery-option-card ${selectedDeliveryOption.id === option.id ? 'selected' : ''}`}
                  >
                    <input
                      type="radio"
                      name="delivery-option"
                      value={option.id}
                      checked={selectedDeliveryOption.id === option.id}
                      onChange={() => setSelectedDeliveryOption(option)}
                    />
                    <div className="delivery-option-content">
                      <div className="delivery-option-header">
                        <span className="delivery-option-title">{option.label}</span>
                        <span className="delivery-option-price">
                          {option.price === 0 ? 'Free (subject to confirmation)' : `Ksh ${option.price.toLocaleString('en-KE')}`}
                        </span>
                      </div>
                      <p className="delivery-option-description">{option.description}</p>
                    </div>
                  </label>
                ))}
              </div>
              <p className="delivery-advisory">
                Free deliveries are subject to communication and confirmation with our logistics team.
              </p>
            </div>

            <div className="checkout-section">
              <h2>Payment Method</h2>
              <div className="payment-methods">
                <label className="payment-option">
                  <input
                    type="radio"
                    name="payment"
                    value="mpesa"
                    checked={paymentMethod === 'mpesa'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  />
                  <div className="payment-option-content">
                    <span className="payment-icon">📱</span>
                    <span>M-Pesa</span>
                  </div>
                </label>
                <label className="payment-option">
                  <input
                    type="radio"
                    name="payment"
                    value="card"
                    checked={paymentMethod === 'card'}
                    onChange={(e) => setPaymentMethod(e.target.value)}
                  />
                  <div className="payment-option-content">
                    <span className="payment-icon">💳</span>
                    <span>Card Payment</span>
                  </div>
                </label>
              </div>

              {paymentMethod === 'mpesa' && (
                <form className="mpesa-form" onSubmit={handleMpesaPayment}>
                  <div className="form-group">
                    <label>M-Pesa Number *</label>
                    <input
                      type="tel"
                      placeholder="0XXXXXXXXX"
                      value={mpesaNumber}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\D/g, '');
                        if (value.length <= 10 && (value === '' || value.startsWith('0'))) {
                          setMpesaNumber(value);
                        }
                      }}
                      required
                      maxLength="10"
                    />
                    <small>Enter your M-Pesa number starting with 0</small>
                  </div>
                  <button type="submit" className="pay-button mpesa-button">
                    Pay with M-Pesa
                  </button>
                </form>
              )}

              {paymentMethod === 'card' && (
                <form className="card-form" onSubmit={handleCardPayment}>
                  <div className="form-group">
                    <label>Card Number *</label>
                    <input
                      type="text"
                      placeholder="1234 5678 9012 3456"
                      value={cardDetails.cardNumber}
                      onChange={(e) => {
                        const value = e.target.value.replace(/\s/g, '').replace(/\D/g, '');
                        const formatted = value.match(/.{1,4}/g)?.join(' ') || value;
                        setCardDetails({...cardDetails, cardNumber: formatted});
                      }}
                      maxLength="19"
                      required
                    />
                  </div>
                  <div className="form-row">
                    <div className="form-group">
                      <label>Expiry Date *</label>
                      <input
                        type="text"
                        placeholder="MM/YY"
                        value={cardDetails.expiryDate}
                        onChange={(e) => {
                          let value = e.target.value.replace(/\D/g, '');
                          if (value.length >= 2) {
                            value = value.slice(0, 2) + '/' + value.slice(2, 4);
                          }
                          setCardDetails({...cardDetails, expiryDate: value});
                        }}
                        maxLength="5"
                        required
                      />
                    </div>
                    <div className="form-group">
                      <label>CVV *</label>
                      <input
                        type="text"
                        placeholder="123"
                        value={cardDetails.cvv}
                        onChange={(e) => {
                          const value = e.target.value.replace(/\D/g, '').slice(0, 3);
                          setCardDetails({...cardDetails, cvv: value});
                        }}
                        maxLength="3"
                        required
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Cardholder Name *</label>
                    <input
                      type="text"
                      value={cardDetails.cardName}
                      onChange={(e) => setCardDetails({...cardDetails, cardName: e.target.value})}
                      required
                    />
                  </div>
                  <button type="submit" className="pay-button card-button">
                    Pay with Card
                  </button>
                </form>
              )}
            </div>

            <div className="checkout-section">
              <h2>Order via WhatsApp</h2>
              <p className="whatsapp-description">
                Prefer to order through WhatsApp? Click the button below to send your order details.
              </p>
              <button className="whatsapp-order-btn" onClick={handleWhatsAppOrder}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z"/>
                </svg>
                Order via WhatsApp
              </button>
            </div>
          </div>

          <div className="checkout-right">
            <div className="order-summary">
              <h2>Order Summary</h2>
              <div className="order-items">
                {availableItems.map(item => {
                  const itemId = item.id || item._id;
                  return (
                    <div key={item.id} className="order-item">
                      <div className="order-item-image-wrapper">
                        {imageErrors[itemId] ? (
                          <div className="order-item-image-placeholder">
                            <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                              <circle cx="8.5" cy="8.5" r="1.5"></circle>
                              <polyline points="21 15 16 10 5 21"></polyline>
                            </svg>
                          </div>
                        ) : (
                          <img 
                            src={getProductImage(item)} 
                            alt={item.name} 
                            className="order-item-image"
                            onError={() => handleImageError(itemId)}
                          />
                        )}
                      </div>
                      <div className="order-item-details">
                        <h4>{item.name}</h4>
                        <p>Qty: {item.quantity}</p>
                        {item.isAvailable === false && (
                          <span className="availability-badge unavailable">Unavailable</span>
                        )}
                      </div>
                      <div className="order-item-price">
                        Ksh {(item.price * item.quantity).toLocaleString('en-KE')}
                      </div>
                      <button 
                        className="remove-item-btn"
                        onClick={() => handleRemoveItem(item.id)}
                        title="Remove item"
                      >
                        ×
                      </button>
                    </div>
                  );
                })}
                {unavailableItems.length > 0 && (
                  <div className="unavailable-items-section">
                    <h4 className="unavailable-title">Unavailable Items (Not included in total)</h4>
                    {unavailableItems.map(item => {
                      const itemId = item.id || item._id;
                      return (
                        <div key={item.id} className="order-item unavailable-item">
                          <div className="order-item-image-wrapper">
                            {imageErrors[itemId] ? (
                              <div className="order-item-image-placeholder">
                                <svg width="60" height="60" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                  <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                                  <circle cx="8.5" cy="8.5" r="1.5"></circle>
                                  <polyline points="21 15 16 10 5 21"></polyline>
                                </svg>
                              </div>
                            ) : (
                              <img 
                                src={getProductImage(item)} 
                                alt={item.name} 
                                className="order-item-image"
                                onError={() => handleImageError(itemId)}
                              />
                            )}
                          </div>
                          <div className="order-item-details">
                            <h4>{item.name}</h4>
                            <p>Qty: {item.quantity}</p>
                            <span className="availability-badge unavailable">Unavailable</span>
                          </div>
                          <div className="order-item-price unavailable-price">
                            Ksh {(item.price * item.quantity).toLocaleString('en-KE')}
                          </div>
                          <button 
                            className="remove-item-btn"
                            onClick={() => handleRemoveItem(item.id)}
                            title="Remove item"
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="order-totals">
                <div className="total-row">
                  <span>Subtotal</span>
                  <span>Ksh {calculateTotal().toLocaleString('en-KE')}</span>
                </div>
                <div className="total-row">
                  <span>Delivery ({selectedDeliveryOption.label})</span>
                  <span>
                    {deliveryCost === 0 ? 'Pending confirmation' : `Ksh ${deliveryCost.toLocaleString('en-KE')}`}
                  </span>
                </div>
                <div className="total-row total-final">
                  <span>Total</span>
                  <span>
                    {deliveryCost === 0
                      ? `Ksh ${calculateTotal().toLocaleString('en-KE')}*`
                      : `Ksh ${(calculateTotal() + deliveryCost).toLocaleString('en-KE')}`}
                  </span>
                </div>
                {deliveryCost === 0 && (
                  <p className="delivery-confirmation-note">
                    *Final total will be confirmed once free delivery is approved.
                  </p>
                )}
              </div>
            </div>
            <div className="checkout-actions">
              <button onClick={() => navigate('/')} className="continue-shopping-btn">
                ← Continue Shopping
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Checkout;

